import React, { useEffect, useState } from 'react'
import {
  CCard,
  CCardBody,
  CCol,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilChartLine,
  cilCloudDownload,
  cilDollar,
  cilFlagAlt,
  cilPeople,
  cilSwapHorizontal,
  cilStar,
} from '@coreui/icons'
import moment from 'moment'
import UserService from 'src/service/UserService'
import PlayerService from 'src/service/PlayerService'
import HeadToHeadService from 'src/service/HeadToHeadService'
import CommonService from 'src/service/CommonService'
import RivalTrackerService from 'src/service/RivalTrackerService'
import ToastComponent from 'src/components/common/TaostComponent'
import { InfoBox } from 'src/components/common/ImportWizard'
import './DownloadCsv.css'

// Each export declares its columns once: the header row, the cell for each record, and the
// column chips on its card all come from the same list.
const EXPORTS = [
  {
    key: 'ranking',
    title: 'Rankings',
    description: 'Every team with its points and rank, overall or for a single round.',
    icon: cilStar,
    tone: 'warning',
    byRound: true,
    fetch: (roundId) => UserService.getRank(roundId).then((result) => result.data?.user_teams),
    fileName: (round) => `Ranking-${round ? `Round-${round.round}` : 'Overall'}`,
    columns: [
      ['Full Name', (row) => row?.userdata?.full_name],
      ['Team Name', (row) => row.my_team_name],
      ['Points', (row) => row.team_points],
      ['Rank', (row) => row.team_rank],
    ],
  },
  {
    key: 'player-stats',
    title: 'Player Stats',
    description: 'Season totals for every player, from fantasy points to hat-tricks.',
    icon: cilChartLine,
    tone: 'primary',
    fetch: () => PlayerService.playerCsvStats().then((result) => result.data),
    fileName: () => 'Player-Stats',
    columns: [
      ['Player Name', (row) => row?.player_name],
      ['Team Name', (row) => row.team_name],
      ['FP', (row) => row.sum_fp],
      ['POS', (row) => row.position_name],
      ['RS', (row) => row.sum_rs],
      ['4S', (row) => row.sum_fours],
      ['6S', (row) => row.sum_sixes],
      ['OVR', (row) => row.sum_overs],
      ['MDN', (row) => row.sum_mdns],
      ['WKS', (row) => row.sum_wks],
      ['CS', (row) => row.sum_cs],
      ['CWK', (row) => row.sum_cwks],
      ['STS', (row) => row.sum_sts],
      ['ROD', (row) => row.sum_rods],
      ['ROA', (row) => row.sum_roas],
      ['DKS', (row) => row.sum_dks],
      ['HT', (row) => row.sum_hattrick],
    ],
  },
  {
    key: 'player-values',
    title: 'Player Values',
    description: 'Each player’s starting and current value, and how far it has moved.',
    icon: cilDollar,
    tone: 'success',
    fetch: () => PlayerService.playerCsvValues().then((result) => result.data),
    fileName: () => 'Player-Values',
    columns: [
      ['Full Name', (row) => row?.full_name],
      ['Starting Value', (row) => row.initial_value],
      ['Current Value', (row) => row.svalue],
      ['Value Change', (row) => row.value_change],
      ['Value Change(%)', (row) => row.value_change_in_percent],
    ],
  },
  {
    key: 'h2h',
    title: 'Head-to-Head',
    description: 'The head-to-head leaderboard: record, win rate and points for and against.',
    icon: cilSwapHorizontal,
    tone: 'info',
    fetch: () => HeadToHeadService.headToHeadLeaderboardCsv().then((result) => result.data),
    fileName: () => 'H2H',
    columns: [
      ['Full Name', (row) => row?.full_name],
      ['Played', (row) => row.played],
      ['Wins', (row) => row.wins],
      ['Losses', (row) => row.losses],
      ['Ties', (row) => row.ties],
      ['Win%', (row) => row.win_percentage],
      ['Points For', (row) => row.points_for],
      ['Points Against', (row) => row.points_against],
      ['Point Difference', (row) => row.point_difference],
      ['Points', (row) => row.points],
    ],
  },
  {
    key: 'members',
    title: 'Members',
    description: 'Everyone in the game with their contact details and verification status.',
    icon: cilPeople,
    tone: 'secondary',
    fetch: () => UserService.getMemberCsvData().then((result) => result.data),
    fileName: () => 'Members',
    columns: [
      ['Full Name', (row) => row?.full_name],
      ['Email', (row) => row.email],
      ['Phone', (row) => row.phone],
      ['Registered', (row) => moment(row.created_at).format('D.MM.YYYY')],
      ['Status', (row) => (row.is_verified === 1 ? 'Verified' : 'Unverified')],
    ],
  },
  {
    key: 'rivals',
    title: 'Rival Tracker',
    description:
      'How many rivals each member is tracking, with when they first and last added one.',
    icon: cilFlagAlt,
    tone: 'danger',
    fetch: () => RivalTrackerService.getAllMembers().then((result) => result.data),
    fileName: () => 'Rival-Tracker',
    columns: [
      ['Member', (row) => row.full_name],
      ['Email', (row) => row.email],
      ['Their Team', (row) => row.team_name],
      ['Rivals Tracked', (row) => row.rival_count],
      ['First Added', (row) => row.first_added_at],
      ['Last Added', (row) => row.last_added_at],
    ],
  },
]

// Chips shown on a card before the rest collapse into "+N more".
const COLUMN_PREVIEW = 6

// Quote any cell that would otherwise split the row: commas, quotes or line breaks.
const csvCell = (value) => {
  if (value === null || value === undefined) return ''
  const text = String(value)
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const buildCsv = (columns, rows) =>
  [
    columns.map(([label]) => csvCell(label)).join(','),
    ...rows.map((row) => columns.map(([, cell]) => csvCell(cell(row))).join(',')),
  ].join('\n')

const saveCsv = (csv, fileName) => {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const ColumnChips = ({ columns }) => {
  const [expanded, setExpanded] = useState(false)
  const labels = columns.map(([label]) => label)
  const shown = expanded ? labels : labels.slice(0, COLUMN_PREVIEW)
  const hidden = labels.length - shown.length

  return (
    <div className="dl-columns">
      {shown.map((label) => (
        <span key={label} className="dl-column">
          {label}
        </span>
      ))}
      {labels.length > COLUMN_PREVIEW && (
        <button type="button" className="dl-column-more" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : `+${hidden} more`}
        </button>
      )}
    </div>
  )
}

const DownloadCsv = () => {
  const [roundList, setRoundList] = useState([])
  const [roundsLoading, setRoundsLoading] = useState(true)
  const [roundId, setRoundId] = useState('0')
  const [gameName, setGameName] = useState('')
  const [downloading, setDownloading] = useState(null)

  useEffect(() => {
    UserService.getRank(0)
      .then((result) => setRoundList(result.data?.round_list || []))
      .catch(() => ToastComponent('Could not load the round list', 'error'))
      .finally(() => setRoundsLoading(false))
    CommonService.getGameName()
      .then((res) => {
        if (res.success) setGameName(res.game_name)
      })
      .catch(() => {})
  }, [])

  const selectedRound = roundList.find((round) => String(round.id) === roundId)
  const fileNameFor = (exp) =>
    `MyClubtap-${gameName || 'Game'}-${exp.fileName(exp.byRound ? selectedRound : undefined)}.csv`

  const download = (exp) => {
    setDownloading(exp.key)
    exp
      .fetch(exp.byRound ? roundId : undefined)
      .then((rows) => {
        if (!rows?.length) {
          ToastComponent(`There is no ${exp.title.toLowerCase()} data to download yet`, 'error')
          return
        }
        saveCsv(buildCsv(exp.columns, rows), fileNameFor(exp))
        ToastComponent(`${exp.title} downloaded`, 'success')
      })
      .catch(() => ToastComponent(`Could not download ${exp.title.toLowerCase()}`, 'error'))
      .finally(() => setDownloading(null))
  }

  return (
    <>
      <div className="ge-page-header">
        <h1 className="ge-page-title">Downloads</h1>
        <p className="ge-page-subtitle">
          Export this game&apos;s data as CSV files to open in Excel or Google Sheets.
        </p>
      </div>

      <InfoBox>
        Each file is built from the live data at the moment you download it, so download again to
        pick up the latest scores.
      </InfoBox>

      <CRow className="g-3">
        {EXPORTS.map((exp) => (
          <CCol md={6} xl={4} key={exp.key}>
            <CCard className="dl-card h-100">
              <CCardBody className="d-flex flex-column">
                <div className="dl-card-head">
                  <div className={`ge-stat-icon ge-stat-icon--${exp.tone}`}>
                    <CIcon icon={exp.icon} size="lg" />
                  </div>
                  <div>
                    <h2 className="dl-card-title">{exp.title}</h2>
                    <p className="dl-card-note">{exp.description}</p>
                  </div>
                </div>

                <div className="dl-section-label">
                  {exp.columns.length} column{exp.columns.length !== 1 ? 's' : ''}
                </div>
                <ColumnChips columns={exp.columns} />

                {exp.byRound && (
                  <div className="mt-3">
                    <CFormLabel htmlFor="dl-round" className="dl-section-label">
                      Round
                    </CFormLabel>
                    <CFormSelect
                      id="dl-round"
                      value={roundId}
                      disabled={roundsLoading}
                      onChange={(e) => setRoundId(e.target.value)}
                    >
                      <option value="0">Overall (all rounds)</option>
                      {roundList.map((round) => (
                        <option key={round.id} value={String(round.id)}>
                          Round {round.round}
                        </option>
                      ))}
                    </CFormSelect>
                    <div className="dl-hint">
                      {roundsLoading
                        ? 'Loading rounds...'
                        : roundList.length
                        ? 'Only rounds that have started are listed.'
                        : 'No round has started yet.'}
                    </div>
                  </div>
                )}

                <div className="dl-card-footer">
                  <span className="dl-file" title={fileNameFor(exp)}>
                    {fileNameFor(exp)}
                  </span>
                  <CLoadingButton
                    color="primary"
                    loading={downloading === exp.key}
                    disabled={downloading !== null && downloading !== exp.key}
                    onClick={() => download(exp)}
                  >
                    <CIcon icon={cilCloudDownload} className="me-1" />
                    Download CSV
                  </CLoadingButton>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>
    </>
  )
}

export default DownloadCsv
